import axios from 'axios';
import { Action, createAction, handleActions } from 'redux-actions';

import { setLocale } from './locale';
import { FULFILLED, PENDING, REJECTED } from './action-type.util';
import {
    DEFAULT_LOCALE,
    getAxiosData,
    getRejectedRespData,
    LS_AUTH_TOKEN,
    LS_DEFAULT_LOCALE,
    SERVER_API_URL,
} from '../config/constants';
import { IUser } from './user.model';

export const ACTION_TYPES = {
    LOGIN: 'auth/LOGIN',
    QR_AUTH: 'auth/QR_AUTH',
    GET_SESSION: 'auth/GET_SESSION',
    LOGOUT: 'auth/LOGOUT',
    CLEAR_AUTH: 'auth/CLEAR_AUTH',
    ERROR_MESSAGE: 'auth/ERROR_MESSAGE',

    CURRENT_USER: 'auth/CURRENT_USER',
};

/*export interface IUser {
    username?: string;
    firstName?: string;
    lastName?: string;
    roleName?: string;
}*/

export interface IAccount {
    langKey: string;
    user: IUser;
    shiftNumber: string;
    activated: boolean;
    authorities: string[];
}

export interface IPermission {
    [key: string]:
        | boolean
        | {
              [key: string]: boolean;
          };
}

export interface IPermissionItem {
    securityObject: string;
    permissions: string[];
}

export interface IAuthState {
    loading: boolean;
    isAuthenticated: boolean;
    loginSuccess: boolean;
    loginError: boolean;
    account: IAccount;
    authToken: string;
    user: IUser;
    permission: IPermission;
    error: any;
    redirectMessage: string;
    sessionHasBeenFetched: boolean;
    logoutUrl: string;
}

const initialState = {
    loading: false,
    isAuthenticated: false,
    loginSuccess: false,
    loginError: false, // Errors returned from server side
    account: {} as IAccount,
    authToken: '',
    user: {} as IUser,
    permission: {} as IPermission,
    error: {}, // Errors returned from server side
    redirectMessage: '',
    sessionHasBeenFetched: false,
    logoutUrl: '',
};

const parsePermissions = (permissions: IPermissionItem[]): IPermission => {
    return (permissions || []).reduce((res: IPermission, el: IPermissionItem) => {
        /*const key = el.securityObject
            .split('.')
            .reduce((resKey: string[], elKey: string, posKey) => {
                resKey.push(posKey ? elKey.charAt(0).toUpperCase() + elKey.slice(1) : elKey);
                return resKey;
            }, [])
            .join('');*/
        const key = el.securityObject.split('.').pop() || '';
        res[key] = (el.permissions || []).reduce((res: any, item: string) => ({ ...res, [item]: true }), {});
        return res;
    }, {});
    // return {};
};

export const displayAuthError = (message) => ({ type: ACTION_TYPES.ERROR_MESSAGE, message });

export const clearAuthToken = () => {
    sessionStorage.removeItem(LS_AUTH_TOKEN);
};

export const getSession = () => async (dispatch, getState) => {
    await dispatch({
        type: ACTION_TYPES.GET_SESSION,
        // payload: axios.get('api/account'),
        payload: getAxiosData(
            Promise.resolve(
                sessionStorage.getItem(LS_AUTH_TOKEN)
                    ? { data: { langKey: 'uk', activated: true, authorities: ['ROLE_ADMIN'], isAuthenticated: true } }
                    : {},
            ),
        ),
    });

    const { account } = getState().auth;
    if (account && account.langKey) {
        await dispatch(setLocale(account.langKey));
    }
};

export const getCurrentUser = createAction(ACTION_TYPES.CURRENT_USER, () =>
    getAxiosData(axios.get(`${SERVER_API_URL}/auth/current-user`)),
);
/* export const getCurrentUser = () => async (dispatch) => {
    try {
        await dispatch({
            type: ACTION_TYPES.CURRENT_USER,
            payload: getAxiosData(axios.get(`${SERVER_API_URL}/auth/current-user`))
        });
    } catch (e) {
        clearAuthToken();
        dispatch({ type: FULFILLED(ACTION_TYPES.LOGOUT) });
    }
}; */

export const login = (login: string, password: string) => async (dispatch) => {
    const result = await dispatch({
        type: ACTION_TYPES.LOGIN,
        payload: getAxiosData(
            axios.post(`${SERVER_API_URL}/auth/login`, { login, password }, { headers: { SkipError: true } }),
        ),
    });

    result.value && sessionStorage.setItem(LS_AUTH_TOKEN, result.value);

    // fetch new csrf token
    dispatch(getSession());
};

export const logout = () => async (dispatch) => {
    await dispatch({
        type: ACTION_TYPES.LOGOUT,
        // payload: getAxiosData(axios.post(`${SERVER_API_URL}/auth/logout`, {})),
    });
    clearAuthToken();

    // fetch new csrf token
    dispatch(getSession());
};

export const clearAuthentication = (messageKey?) => (dispatch) => {
    clearAuthToken();
    messageKey && dispatch(displayAuthError(messageKey));
    dispatch({ type: ACTION_TYPES.CLEAR_AUTH });
};

export const accessDenied = (messageKey) => (dispatch) => {
    dispatch(displayAuthError(messageKey));
};

/* Reducer function */
export default handleActions<IAuthState, any>(
    {
        [ACTION_TYPES.CLEAR_AUTH]: (): IAuthState => ({ ...initialState, sessionHasBeenFetched: true }),

        [ACTION_TYPES.LOGOUT]: (): IAuthState => ({ ...initialState, sessionHasBeenFetched: true }),

        [PENDING(ACTION_TYPES.LOGIN)]: (state: IAuthState): IAuthState => ({
            ...state,
            loading: true,
            loginSuccess: true,
            loginError: false,
        }),
        [REJECTED(ACTION_TYPES.LOGIN)]: (state: IAuthState, action: Action<any>): IAuthState => ({
            ...initialState,
            error: getRejectedRespData(action.payload),
            loginError: true,
        }),
        [FULFILLED(ACTION_TYPES.LOGIN)]: (state: IAuthState, action: Action<string>): IAuthState => ({
            ...state,
            authToken: action.payload,
            loading: false,
            loginSuccess: true,
            loginError: false,
        }),

        [PENDING(ACTION_TYPES.GET_SESSION)]: (state: IAuthState): IAuthState => ({
            ...state,
            loading: true,
            sessionHasBeenFetched: false,
        }),
        [REJECTED(ACTION_TYPES.GET_SESSION)]: (state: IAuthState, action: Action<any>): IAuthState => ({
            ...state,
            error: getRejectedRespData(action.payload),
            loginError: true,
            loading: false,
            isAuthenticated: false,
            sessionHasBeenFetched: true,
        }),
        [FULFILLED(ACTION_TYPES.GET_SESSION)]: (state: IAuthState, action: Action<any>): IAuthState => {
            const isAuthenticated = action.payload && action.payload.activated;
            return {
                ...state,
                isAuthenticated,
                loading: false,
                sessionHasBeenFetched: true,
                account: action.payload,
            };
        },

        [PENDING(ACTION_TYPES.CURRENT_USER)]: (state: IAuthState): IAuthState => ({
            ...state,
            loading: true,
            sessionHasBeenFetched: false,
        }),
        [REJECTED(ACTION_TYPES.CURRENT_USER)]: (state: IAuthState, action: Action<any>): IAuthState => ({
            ...state,
            loading: false,
            sessionHasBeenFetched: true,
            error: getRejectedRespData(action.payload),
        }),
        [FULFILLED(ACTION_TYPES.CURRENT_USER)]: (state: IAuthState, action: Action<any>): IAuthState => {
            const { permissions, ...user } = action.payload;

            user.defaultLocale || (user.defaultLocale = DEFAULT_LOCALE);
            localStorage.setItem(LS_DEFAULT_LOCALE, user.defaultLocale);

            return {
                ...state,
                loading: false,
                user,
                sessionHasBeenFetched: true,
                permission: parsePermissions(permissions),
            };
        },
    },
    initialState,
);
